import { useState, useEffect, useRef } from 'react';

/**
 * UniversalSearchBar — a generic, reusable search input.
 *
 * Give it a dataset and the list of fields (dot-paths supported for nested
 * objects) you want to search across, and it will call `onResults` with the
 * filtered array every time the user types (debounced) or the source data
 * changes. It does no rendering of results itself, so it can sit on top of
 * any table/list/grid in the app.
 *
 * ─── Usage ──────────────────────────────────────────────────────────────────
 *   const [visible, setVisible] = useState(myData);
 *
 *   <UniversalSearchBar
 *       data={myData}
 *       searchKeys={['name', 'email', 'address.city']}
 *       onResults={setVisible}
 *       placeholder="Search customers…"
 *   />
 *
 *   // then render `visible` instead of `myData`
 *
 * ─── Props ──────────────────────────────────────────────────────────────────
 *   data        array<object>     Required. The full dataset to search over.
 *   searchKeys  string[]          Required. Keys to match against. Supports
 *                                 dot-notation for nested fields, e.g.
 *                                 'address.city'.
 *   onResults   (filtered) => {}  Required. Called with the filtered array.
 *   placeholder string            Optional. Input placeholder text.
 *   debounceMs  number            Optional. Debounce delay, default 250ms.
 *   className   string            Optional. Extra classes on the wrapper.
 *   autoFocus   boolean           Optional. Focus the input on mount.
 */

function getValue(obj, path) {
    return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

export default function UniversalSearchBar({
    data = [],
    searchKeys = [],
    onResults,
    placeholder = 'Search…',
    debounceMs = 250,
    className = '',
    autoFocus = false,
}) {
    const [term, setTerm] = useState('');
    const debounceRef = useRef(null);
    const keysSignature = searchKeys.join('|');

    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            const q = term.trim().toLowerCase();
            if (!q) {
                onResults?.(data);
                return;
            }
            const filtered = data.filter((row) =>
                searchKeys.some((key) => {
                    const val = getValue(row, key);
                    return val != null && String(val).toLowerCase().includes(q);
                })
            );
            onResults?.(filtered);
        }, debounceMs);

        return () => clearTimeout(debounceRef.current);
    }, [term, data, keysSignature, debounceMs]);

    return (
        <div className={`relative flex items-center ${className}`}>
            <svg
                className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
            </svg>
            <input
                type="text"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder={placeholder}
                autoFocus={autoFocus}
                className="w-full pl-9 pr-8 py-2 text-xs rounded-xl border border-slate-200 bg-white text-slate-800
                           focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition
                           placeholder-slate-400"
            />
            {term && (
                <button
                    type="button"
                    onClick={() => setTerm('')}
                    className="absolute right-2.5 w-4 h-4 flex items-center justify-center rounded-full
                               text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition text-xs"
                    aria-label="Clear search"
                >✕</button>
            )}
        </div>
    );
}